import moment from "moment";

import {
  formatCompactNumber,
  formatCurrency,
  formatPercent,
} from "../../../../../utils";
import {
  LiabilitiesDetails,
  LiabilityBalanceHistoryPoint,
  LiabilityRateTypes,
} from "../../../types";
import { LegendEntry, TrendChartData, TrendCopy, TrendSeries } from "./types";
import { currentRateOf } from "../../../helpers";

// Padding above and below the rate range so the zoomed axis never clips the line.
const RATE_AXIS_PADDING_RATIO = 0.35;
const MIN_RATE_AXIS_PADDING = 0.5;

// One tick per calendar year, regardless of the underlying point density.
export const buildYearTickInterval = (
  labels: string[],
): ((value: unknown, index: number) => boolean) => {
  const firstIndexPerYear = new Map<string, number>();
  labels.forEach((label, index) => {
    const year = moment(label).format("YYYY");
    if (!firstIndexPerYear.has(year)) {
      firstIndexPerYear.set(year, index);
    }
  });
  const tickIndexes = new Set(firstIndexPerYear.values());
  return (_value: unknown, index: number): boolean => tickIndexes.has(index);
};

/** Maps the outstanding-balance series into chart labels and values. */
export const buildBalanceChartData = (
  points: LiabilityBalanceHistoryPoint[],
): TrendChartData => {
  return {
    labels: points.map((point) => point.date),
    values: points.map((point) => point.balance),
  };
};

// The y-axis is zoomed to the rate's range for readability.
export const buildRateChartData = (
  liability: LiabilitiesDetails,
): TrendChartData => {
  const history = [...(liability.interestRateHistory ?? [])];
  if (history.length === 0) {
    return { labels: [], values: [] };
  }

  const labels = history.map((entry) => entry.effectiveDate);
  const values = history.map((entry) => entry.interestRate);

  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min;
  const padding =
    range === 0 ? MIN_RATE_AXIS_PADDING : range * RATE_AXIS_PADDING_RATIO;

  return {
    labels,
    values,
    yMin: Math.max(0, min - padding),
    yMax: max + padding,
  };
};

/** Returns the payoff year shown in the balance subtitle. */
export const getPayoffYear = (endDate?: string): string => {
  if (!endDate) {
    return "";
  }
  return moment(endDate).format("YYYY");
};

/** Resolves the current rate type to describe the rate series in the subtitle. */
export const getCurrentRateType = (
  liability: LiabilitiesDetails,
): LiabilityRateTypes | undefined => {
  return currentRateOf(liability.interestRateHistory)?.rateType;
};

/** Builds the card title/subtitle copy for the active tab. */
export const buildTrendCopy = (
  isBalance: boolean,
  isCompact: boolean,
  liability: LiabilitiesDetails,
): TrendCopy => {
  if (isBalance) {
    const payoffYear = getPayoffYear(liability.endDate);
    const subtitle = isCompact
      ? `Outstanding balance, heading to zero by ${payoffYear}`
      : `Outstanding balance since origination, heading to zero by ${payoffYear}`;
    return { title: "Balance over time", subtitle };
  }

  const isVariable =
    getCurrentRateType(liability) === LiabilityRateTypes.VARIABLE;
  const rateLabel = isVariable ? "Variable" : "Fixed";
  const rateWord = isVariable ? "variable" : "fixed";
  const subtitle = isCompact
    ? `${rateLabel} rate since origination`
    : `Your ${rateWord} rate since origination`;
  return { title: "Interest rate", subtitle };
};

/** Builds the legend entries for the active tab. */
export const buildLegendItems = (
  isBalance: boolean,
  balanceColor: string,
  rateColor: string,
): LegendEntry[] => {
  if (isBalance) {
    return [
      { label: "Outstanding balance", color: balanceColor, style: "solid" },
    ];
  }
  return [
    { label: "Interest rate", color: rateColor, style: "solid" },
    { label: "Rate reset", color: rateColor, style: "dot" },
  ];
};

/** Builds the LineChart series config for the active tab. */
export const buildTrendSeries = (
  isBalance: boolean,
  values: number[],
  color: string,
  lastIndex: number,
): TrendSeries[] => {
  const curve: "linear" | "stepAfter" = isBalance ? "linear" : "stepAfter";
  const showMark: boolean | ((params: { index: number }) => boolean) = isBalance
    ? ({ index }) => index === lastIndex
    : true;
  const valueFormatter = isBalance
    ? (value: number | null) => (value === null ? "" : formatCurrency(value))
    : (value: number | null) => formatPercent(value, 3);
  return [{ data: values, area: true, color, curve, showMark, valueFormatter }];
};

/** Builds the y-axis tick formatter for the active tab. */
export const buildYAxisFormatter = (
  isBalance: boolean,
): ((value: number) => string) => {
  if (isBalance) {
    return (value: number) => `${formatCompactNumber(value)} €`;
  }
  return (value: number) => `${value.toFixed(2)}%`;
};
